import React__default, { useRef, useState, useCallback, useEffect } from 'react';
import { useMountedRef } from '@shopify/react-hooks';
import classNames from 'classnames';
import { variationName } from '../../../../utilities/css.mjs';
import { isEdgeUserAgent } from '../../../../utilities/user-agent.mjs';
import { requiresPortal, isShrinkingToDefault, getDimensions, isInitialState, getWrapperHeight, getHeight, requiresDimensions, getComboStyles, getWrapperStyles, getNextSize, isExpandingFromDefault, isExpandingFromExpanded } from './utilities.mjs';
import { SizeValue, AnimationState, DimensionProperty } from './types.mjs';
import styles from './ResizeableCombo.scss.mjs';
import { useDimensions } from './hooks/useDimensions.mjs';
import { Header } from './components/Header/Header.mjs';
import { SizeSwitcher } from './components/SizeSwitcher/SizeSwitcher.mjs';
import { FramePortal } from '../../../FramePortal/FramePortal.mjs';
import { FrameAreaValue } from '../../../FrameProvider/types.mjs';
import { Backdrop } from './components/Backdrop/Backdrop.mjs';
import { Combo } from '../Combo/Combo.mjs';

const EMPTY_HTML_MARKUP = '';
function ResizeableCombo({
  children,
  title,
  focused,
  toolbar,
  footer,
  fullWidth,
  unframed,
  hideFocusRing,
  readOnly,
  onTransitionEnd,
  tone
}) {
  const [wrapperRef, childrenRef, comboDimensions, wrapperHeight, childrenHeight, updateDimensions, resetDimensions] = useDimensions();
  const mountedRef = useMountedRef();
  const cloneTargetRef = useRef(null);
  const [size, setSize] = useState(SizeValue.Default);
  const [animation, setAnimation] = useState(AnimationState.Idle);
  const [shouldRenderWithinPortal, setShouldRenderWithinPortal] = useState(false);
  const [clonedContent, setClonedContent] = useState(EMPTY_HTML_MARKUP);
  const handleClose = () => {
    if (childrenRef.current) {
      setClonedContent(childrenRef.current.innerHTML);
    }
    setSize(SizeValue.Default);
    setAnimation(AnimationState.BeforeShrinking);
  };
  const handleSizeChange = () => {
    const sizeData = {
      previousSize: size,
      nextSize: getNextSize(size)
    };
    if (isExpandingFromDefault(sizeData)) {
      updateDimensions();
      setAnimation(AnimationState.BeforeExpanding);
    } else {
      setAnimation(isExpandingFromExpanded(sizeData) ? AnimationState.Expanding : AnimationState.Shrinking);
    }
    setSize(sizeData.nextSize);
  };
  const handleTransitionEnd = useCallback(event => {
    // All properties are transitioning at the same rate,
    // so we pick just one to prevent multiple events.
    if (event.propertyName === DimensionProperty.Width) {
      setAnimation(AnimationState.Idle);
      onTransitionEnd == null ? void 0 : onTransitionEnd();
    }
  }, [onTransitionEnd]);
  const nextPortalState = requiresPortal({
    size,
    animation
  });
  const shouldUpdatePortalState = shouldRenderWithinPortal !== nextPortalState;
  useEffect(() => {
    if (!shouldUpdatePortalState) {
      return;
    }

    // Need to wait a single frame before rendering through
    // the portal, giving the dimensions enough time to register.
    requestAnimationFrame(() => {
      if (mountedRef.current) {
        setShouldRenderWithinPortal(nextPortalState);
      }
    });
  }, [mountedRef, nextPortalState, shouldUpdatePortalState]);
  useEffect(() => {
    // If `height` has shrunk when transitioning back to Default,
    // the scroll position may have changed, so we need to
    // conditionally re-set the `top` property.
    if (isShrinkingToDefault({
      size,
      animation
    })) {
      var _comboDimensions$curr;
      const currentComboDimensions = getDimensions(wrapperRef.current);
      if ((currentComboDimensions == null ? void 0 : currentComboDimensions.top) !== ((_comboDimensions$curr = comboDimensions.current) == null ? void 0 : _comboDimensions$curr.top)) {
        comboDimensions.current = currentComboDimensions;
      }
    }
    if (isInitialState({
      size,
      animation
    }) && comboDimensions.current) {
      resetDimensions();
    }
  }, [size, animation, comboDimensions, wrapperRef, resetDimensions]);
  const shouldClearClonedContent = animation !== AnimationState.BeforeShrinking && clonedContent !== EMPTY_HTML_MARKUP;
  useEffect(() => {
    if (shouldClearClonedContent) {
      setClonedContent(EMPTY_HTML_MARKUP);
    }
  }, [shouldClearClonedContent]);
  useEffect(() => {
    if (!shouldRenderWithinPortal) {
      return;
    }
    if (animation === AnimationState.BeforeExpanding) {
      requestAnimationFrame(() => {
        if (mountedRef.current) {
          setAnimation(AnimationState.Expanding);
        }
      });
    }
    if (animation === AnimationState.BeforeShrinking) {
      var _wrapperHeight$curren, _childrenHeight$curre, _getHeight;
      wrapperHeight.current = getWrapperHeight({
        wrapper: (_wrapperHeight$curren = wrapperHeight.current) != null ? _wrapperHeight$curren : 0,
        children: (_childrenHeight$curre = childrenHeight.current) != null ? _childrenHeight$curre : 0,
        cloneTarget: (_getHeight = getHeight(cloneTargetRef.current)) != null ? _getHeight : 0
      });
      setAnimation(AnimationState.Shrinking);
    }
  }, [mountedRef, animation, shouldRenderWithinPortal, wrapperHeight, childrenHeight, cloneTargetRef]);
  const headerMarkup = shouldRenderWithinPortal ? /*#__PURE__*/React__default.createElement(Header, {
    show: size !== SizeValue.Default,
    title: title,
    onClose: handleClose
  }) : null;
  const footerChildren = footer ? /*#__PURE__*/React__default.createElement("div", {
    className: styles.FooterChildren
  }, footer) : null;
  const footerMarkup = /*#__PURE__*/React__default.createElement(React__default.Fragment, null, footerChildren, /*#__PURE__*/React__default.createElement("div", {
    className: styles.SizeSwitcherWrapper
  }, /*#__PURE__*/React__default.createElement(SizeSwitcher, {
    size: size,
    onChange: handleSizeChange
  })));
  const resizableComboClassNames = classNames(styles.ResizeableCombo, styles[variationName('size', size)], styles[variationName('animation', animation)]);
  const comboStyles = requiresDimensions({
    size,
    animation
  }) ? getComboStyles({
    comboDimensions: comboDimensions.current,
    wrapperHeight: wrapperHeight.current
  }) : undefined;
  const comboTransitionHandler = animation !== AnimationState.Idle && !isEdgeUserAgent() ? handleTransitionEnd : undefined;

  // TODO: We can remove isEdgeUserAgent() once we only support Chromium Edge.
  // For now, we can't register the event listener on Edge as it cannot
  // transition a `calc()` value, causing the event to never fire.
  // https://github.com/Shopify/online-store-ui/issues/1536
  const comboMarkup = /*#__PURE__*/React__default.createElement(Combo, {
    ref: childrenRef,
    focused: focused,
    header: headerMarkup,
    toolbar: toolbar,
    footer: footerMarkup,
    fullWidth: fullWidth,
    unframed: unframed,
    hideFocusRing: hideFocusRing,
    readOnly: readOnly,
    className: resizableComboClassNames,
    style: comboStyles,
    onTransitionEnd: comboTransitionHandler,
    tone: tone
  }, children);

  // TODO: Add aria-labelledby with id generated by uniqueIdFactory
  // https://github.com/Shopify/online-store-ui/issues/1031
  return /*#__PURE__*/React__default.createElement("div", {
    className: styles.Wrapper,
    style: getWrapperStyles(wrapperHeight.current),
    ref: wrapperRef
  }, /*#__PURE__*/React__default.createElement("div", {
    className: styles.CloneTarget,
    ref: cloneTargetRef,
    "aria-hidden": "true",
    tabIndex: -1
    // TODO: Consider storing in state instead.
    // https://github.com/Shopify/online-store-ui/issues/1532
    // eslint-disable-next-line @typescript-eslint/naming-convention
    ,
    dangerouslySetInnerHTML: {
      __html: clonedContent
    }
  }), shouldRenderWithinPortal ? /*#__PURE__*/React__default.createElement(FramePortal, {
    destination: FrameAreaValue.Frame
  }, /*#__PURE__*/React__default.createElement(Backdrop, {
    show: size !== SizeValue.Default,
    fullscreen: size === SizeValue.Fullscreen
  }), comboMarkup) : comboMarkup);
}

export { ResizeableCombo };
